import { toApiError, type ApiError } from '../../core/api/api-error';

/**
 * The optimistic-write port a store adapts itself to, generalised from `features/home/home.store.ts`'s
 * original `mutate` (research R-008; T023) so `features/containers/` can reuse the same write shape
 * over its own data type. `mutate` below knows nothing about `HomeState`, `HomeData`, or any other
 * feature's shape — only these five operations.
 */
export interface OptimisticHost<TData> {
  /** False while the page has nothing ready to write against (loading, error, first run). */
  readonly isReady: () => boolean;
  /** Ids currently in flight — read live, so a concurrent change is always seen. */
  readonly pending: () => readonly number[];
  /** The data as it stands right now. */
  readonly data: () => TData;
  /** Replaces the data the host holds. */
  readonly setData: (data: TData) => void;
  /** Marks `id` in flight and clears the last-refused problem, in one state change. */
  readonly begin: (id: number) => void;
  /** Records a refusal, or a failed follow-up re-read, as the page's current problem. */
  readonly fail: (problem: ApiError) => void;
  /** Un-marks `id` as in flight, however the write ended. */
  readonly release: (id: number) => void;
}

/** One of the reads a successful write can invalidate, and how its answer folds back into the data. */
export interface Reread<TData, TResult> {
  readonly read: () => Promise<TResult>;
  readonly apply: (data: TData, result: TResult) => TData;
}

/**
 * Which reads a successful write invalidated — either fixed, or read off the server's answer.
 *
 * The second form exists for one case (012 T033): completing a recurring occurrence answers with the
 * occurrence it spawned, and a spawn can land in the tickler, which a completion otherwise never
 * touches. Deciding from the response rather than from the successor's own dates keeps the page out
 * of the availability rules — it notices *that* something was spawned, never where it belongs.
 */
export type RereadPlan<TData, TCallResult, TRereadResult> =
  | readonly Reread<TData, TRereadResult>[]
  | ((result: TCallResult) => readonly Reread<TData, TRereadResult>[]);

/**
 * How one write ended, for a caller that must not attribute another row's refusal to its own write.
 *
 * Three values and not two, because two of them are only distinguishable from *inside*:
 *   - `not-attempted` — the guard refused: the page was not ready, or this row already had a write
 *     in flight. Announcing a move for this is the same class of lie as reporting one that
 *     happened as refused.
 *   - `refused` — the server said no and the row was rolled back.
 *   - `written` — the write landed. **Including when the follow-up re-read then failed**: the page
 *     records that as its current problem, but the move itself really happened.
 *
 * `host.fail` is called on the last two, which is why an outside observer of the host cannot
 * classify these and this function has to say so itself (`/simplify`, altitude pass).
 */
export type WriteOutcome = 'written' | 'refused' | 'not-attempted' | 'waiting';

/**
 * The shape every optimistic change on an actions page has (moved unchanged from
 * `features/home/home.store.ts`; research R-008; T023).
 *
 * The optimistic step is for the hand — a change must land the instant it is made. The server's
 * answer is what the page ends up holding: on success the affected views are re-read, because a
 * change can affect something the page has not seen yet (completing a blocker, for instance). On
 * refusal the page is put back as the server last described it, and the refusal is surfaced.
 *
 * Both steps act on the data as it is *at the time*, and touch only this id's row. They must not
 * restore a whole-page snapshot taken when the change started: `pending` is per id, so two changes to
 * different rows overlap freely, and a snapshot rollback would undo whatever the other one committed
 * in between — un-ticking an action the server has already completed.
 */
export async function attempt<TData, TCallResult, TRereadResult>(
  host: OptimisticHost<TData>,
  id: number,
  optimistic: (data: TData) => TData,
  rollback: (data: TData) => TData,
  call: () => Promise<TCallResult>,
  settle: (data: TData, result: TCallResult) => TData,
  reread: RereadPlan<TData, TCallResult, TRereadResult>,
): Promise<WriteOutcome> {
  if (!host.isReady() || host.pending().includes(id)) return 'not-attempted';

  host.begin(id);
  host.setData(optimistic(host.data()));

  let result: TCallResult;
  try {
    result = await call();
  } catch (error) {
    host.setData(rollback(host.data())); // this row only — another change may have landed meanwhile
    host.fail(toApiError(error));
    host.release(id);
    return 'refused';
  }

  try {
    const reads = typeof reread === 'function' ? reread(result) : reread;
    const results = await Promise.all(reads.map((r) => r.read()));
    let next = settle(host.data(), result);
    reads.forEach((r, i) => {
      next = r.apply(next, results[i]);
    });
    host.setData(next);
  } catch (error) {
    // The change itself succeeded; only the re-read failed. Say so rather than undoing it —
    // **and report it as `written`, because it was.** A caller deciding whether to announce a
    // move must not read a failed follow-up read as a refused write: the row really did go where
    // it was sent. This is exactly the distinction an outside observer of `fail` cannot make,
    // which is why the outcome is returned from in here rather than inferred from the host.
    host.fail(toApiError(error));
  } finally {
    host.release(id);
  }
  return 'written';
}

/**
 * The same write, for the callers that do not ask how it ended.
 *
 * Sixteen of the seventeen call sites are `return mutate(...)` inside a method declared
 * `Promise<void>`, and none reads a result — so widening the shared port would have been sixteen
 * mechanical edits for one caller's benefit. This keeps them untouched.
 */
export function mutate<TData, TCallResult, TRereadResult>(
  host: OptimisticHost<TData>,
  id: number,
  optimistic: (data: TData) => TData,
  rollback: (data: TData) => TData,
  call: () => Promise<TCallResult>,
  settle: (data: TData, result: TCallResult) => TData,
  reread: RereadPlan<TData, TCallResult, TRereadResult>,
): Promise<void> {
  return attempt(host, id, optimistic, rollback, call, settle, reread).then(() => undefined);
}

/**
 * 036 T090 (issue 239; research R-018) — what `submit`'s own dispatch answers with, structurally the
 * same three shapes `core/outbox/`'s `SubmitOutcome` carries (a `SubmitOutcome<T>` is assignable
 * here as-is) — but defined independently, on purpose: this file's own header comment already
 * states the rule a hard import of `core/outbox/`/`core/reach/` would break — "`mutate` knows
 * nothing about HomeData, or any other feature's shape — only these five operations." `submit`
 * extends that same generality to a write that can wait, rather than binding this file to the
 * outbox's own module.
 */
export type DispatchOutcome<TCallResult> =
  | { readonly kind: 'answered'; readonly result: TCallResult }
  | { readonly kind: 'waiting' }
  | { readonly kind: 'refused'; readonly problem: ApiError };

/**
 * `mutate`'s own write, extended with the outcome a connectionless write needs (research R-018):
 *
 *  - **reachable, answered** — exactly `attempt`'s own path (optimistic step, then `settle` and the
 *    re-reads). Every existing `mutate`/`attempt` caller is untouched; this is a separate function.
 *  - **reachable, no answer** — the optimistic step already ran (the hand does not wait), and is
 *    rolled back; the mark a caller draws for `waiting` is `core/outbox/`'s own doing
 *    (`useUnsentFor`), not this file's.
 *  - **not reachable** — the optimistic step is never applied at all, not applied-then-rolled-back:
 *    a caller counting `host.setData` calls is what tells the two apart (`optimistic.spec.ts`'s own
 *    mutation for this — "the optimistic step run while unreachable" — turns red exactly there).
 *
 * `reachable` is a predicate, read fresh at the moment the write is attempted, never cached —
 * `core/reach/`'s own `reachState() === 'reachable'`, in production; a fake `() => boolean` in every
 * test here, for the same reason `dispatch` is a fake and never a real `submitChange()`.
 */
export async function submit<TData, TCallResult, TRereadResult>(
  host: OptimisticHost<TData>,
  id: number,
  optimistic: (data: TData) => TData,
  rollback: (data: TData) => TData,
  reachable: () => boolean,
  dispatch: () => Promise<DispatchOutcome<TCallResult>>,
  settle: (data: TData, result: TCallResult) => TData,
  reread: RereadPlan<TData, TCallResult, TRereadResult>,
): Promise<WriteOutcome> {
  if (!host.isReady() || host.pending().includes(id)) return 'not-attempted';

  host.begin(id);
  const appliedOptimistically = reachable();
  if (appliedOptimistically) host.setData(optimistic(host.data()));

  let outcome: DispatchOutcome<TCallResult>;
  try {
    outcome = await dispatch();
  } catch (error) {
    if (appliedOptimistically) host.setData(rollback(host.data()));
    host.fail(toApiError(error));
    host.release(id);
    return 'refused';
  }

  if (outcome.kind === 'waiting') {
    if (appliedOptimistically) host.setData(rollback(host.data()));
    host.release(id);
    return 'waiting';
  }

  if (outcome.kind === 'refused') {
    if (appliedOptimistically) host.setData(rollback(host.data()));
    host.fail(outcome.problem);
    host.release(id);
    return 'refused';
  }

  try {
    const reads = typeof reread === 'function' ? reread(outcome.result) : reread;
    const results = await Promise.all(reads.map((r) => r.read()));
    let next = settle(host.data(), outcome.result);
    reads.forEach((r, i) => {
      next = r.apply(next, results[i]);
    });
    host.setData(next);
  } catch (error) {
    // Same rule as `attempt`'s own: the write itself succeeded, only the re-read failed.
    host.fail(toApiError(error));
  } finally {
    host.release(id);
  }
  return 'written';
}

/**
 * A write that has **already succeeded** somewhere else, folded into the page: adopt the action the
 * server answered with, then re-read whatever that change could have invalidated.
 *
 * 016's editor is the case (code review). It owns its own PATCH — a refusal has to appear beside the
 * form the practitioner is still looking at, not in the page banner — so what reaches the store is a
 * committed fact, not an attempt. `mutate` is the wrong shape for that in two ways, and the second
 * is a defect rather than an awkwardness:
 *
 *  - There is nothing to be optimistic about, so `call`, `rollback` and `settle` are all fakes.
 *  - **Its guard would silently drop the write.** `mutate` refuses a row that is already `pending`,
 *    which is right for a second *optimistic* change (two in-flight guesses about one row cannot
 *    both be rolled back correctly) and wrong here: a star still in flight cannot un-commit an edit
 *    the server has already stored. Dropped, the practitioner is left looking at the old values with
 *    no error to explain them, and on a container page an action that has just been re-filed
 *    elsewhere stays in the list until a reload.
 *
 * So the fold-in is unconditional in `pending`, and it borrows the mark rather than owning it: it
 * marks the row only if nothing else had, and releases only what it marked. Releasing another
 * write's mark would un-disable the row's controls while that write is still in flight.
 *
 * `isReady` is still honoured — a page with no data to fold into has nowhere to put the action.
 */
export async function foldIn<TData, TRereadResult>(
  host: OptimisticHost<TData>,
  id: number,
  adopt: (data: TData) => TData,
  reread: readonly Reread<TData, TRereadResult>[],
): Promise<void> {
  if (!host.isReady()) return;

  const marked = !host.pending().includes(id);
  if (marked) host.begin(id);
  host.setData(adopt(host.data()));

  try {
    const results = await Promise.all(reread.map((r) => r.read()));
    let next = host.data();
    reread.forEach((r, i) => {
      next = r.apply(next, results[i]);
    });
    host.setData(next);
  } catch (error) {
    // The edit itself succeeded; only the re-read failed. Say so rather than undoing it.
    host.fail(toApiError(error));
  } finally {
    if (marked) host.release(id);
  }
}
